import * as Toggle from '@radix-ui/react-toggle-group'
import { Link } from '@tanstack/react-router'
import { CantileverButton } from 'components/CantileverButton'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { SortedAnnotationsReturnType } from 'features/kestrelViewer/hooks/useSortAnnotations'
import { useKestrel } from 'features/kestrelViewer/KestrelProvider'
import { AnnotationEntry } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/AnnotationEntry'
import { MarkupReorderTarget } from 'features/kestrelViewer/kestrelViewerMarkup/MarkupReorderTarget'
import { ToggleAllVisibilityButton } from 'features/kestrelViewer/kestrelViewerMarkup/ToggleAllVisibilityButton'
import { CollapseButton } from 'features/kestrelViewer/kestrelViewerMarkup/utils/CollapseButton'
import { ConfirmationModal } from 'features/kestrelViewer/ui/popupMenu/shared/ConfirmationModal'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { KVAnnotation } from 'types'

import { AnnotationGroupLabel } from './AnnotationGroupLabel'
import { AnnotationSortButton } from './AnnotationSortButton'
import { BatchAnnotationActions } from './BatchAnnotationActions'
import { useAnnotationSelection } from './useAnnotationSelection'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 12px;
`

const EntriesList = styled(Toggle.Root)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SectionTitleRow = styled.div<{ $isDragOver?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  position: sticky;
  top: -8px; /* Extend above the container to cover any gap */
  margin-top: -8px; /* Pull the element up to cover the gap */
  min-height: 2.5rem;
  background-color: ${({ theme, $isDragOver }) =>
    $isDragOver
      ? 'rgba(76, 175, 80, 0.2)'
      : theme.forecast.background2}; /* This matches the sidebar background */
  border: ${({ $isDragOver }) =>
    $isDragOver ? '2px solid rgba(76, 175, 80, 0.5)' : '2px solid transparent'};
  border-radius: 4px;
  transition: all 0.2s ease;
  z-index: 10;
`

const SectionTitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`

export const SectionTitle = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.forecast.activeText};
  font-weight: bold;
  cursor: default;
  user-select: none;
`

type AnnotationSectionProps = {
  sectionAnnotations: SortedAnnotationsReturnType
  sectionTitle: string
  batchActions?: boolean
  historical?: boolean
}

export const AnnotationSection: FC<AnnotationSectionProps> = ({
  sectionAnnotations,
  batchActions,
  sectionTitle,
  historical,
}) => {
  const {
    selectedAnnotationIds,
    isAnnotationsSectionDragOver,
    showBatchDeleteConfirm,
    batchDeleteMessage,
    handleAnnotationClick,
    handleClearSelection,
    handleDropAnnotations,
    handleReorderAnnotations,
    handleAnnotationsSectionDragOver,
    handleAnnotationsSectionDragLeave,
    handleAnnotationsSectionDrop,
    handleBatchDelete,
    handleBatchDeleteConfirm,
    handleBatchDeleteCancel,
  } = useAnnotationSelection()

  const {
    selectedMarkup,
    valueOverrides,
    gatekeeper,
    inspectionType,
    kestrelModel,
  } = useKestrel()
  const [isCollapsed, setIsCollapsed] = useState(false)

  const [hiddenGroups, setHiddenGroups] = useState<string[]>([])
  const ungroupedAnnotations = sectionAnnotations.ungroupedMarkups
  const groupedAnnotations = sectionAnnotations.groupedMarkups

  const renderAnnotations = () => {
    if (isCollapsed) return

    return (
      <React.Fragment>
        {ungroupedAnnotations.length > 0 && (
          <EntriesList
            type='single'
            aria-label='Select annotation'
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {ungroupedAnnotations.map((a: KVAnnotation) => (
              <AnnotationEntry
                key={a.id}
                annotation={a}
                selectedMarkupId={selectedMarkup?.id}
                isMultiSelected={selectedAnnotationIds.has(a.id)}
                selectedAnnotationIds={selectedAnnotationIds}
                onMultiSelectClick={(isShiftClick) =>
                  handleAnnotationClick(a.id, isShiftClick)
                }
                onReorder={handleReorderAnnotations}
              />
            ))}
            {/* Add drop zone at the end for dropping after the last item */}
            {batchActions && ungroupedAnnotations.length > 0 && (
              <MarkupReorderTarget
                dataKey='annotationIds'
                onDrop={(data) => {
                  const ids = JSON.parse(data)
                  const lastAnnotation =
                    ungroupedAnnotations[ungroupedAnnotations.length - 1]
                  handleReorderAnnotations(ids, lastAnnotation.id, 'after')
                }}
              />
            )}
          </EntriesList>
        )}

        {Object.entries(groupedAnnotations).map(([group, annotationGroup]) => (
          <React.Fragment key={group}>
            <AnnotationGroupLabel
              group={valueOverrides[group] || group}
              annotations={annotationGroup}
              setHiddenGroups={setHiddenGroups}
              onDropAnnotations={handleDropAnnotations}
              historical={historical}
            />
            {!hiddenGroups.includes(valueOverrides[group]) &&
              !hiddenGroups.includes(group) && (
                <EntriesList
                  type='single'
                  aria-label='Select annotation'
                  onClick={(event) => {
                    event.stopPropagation()
                  }}>
                  {annotationGroup.map((a: KVAnnotation) => (
                    <AnnotationEntry
                      key={a.id}
                      annotation={a}
                      selectedMarkupId={selectedMarkup?.id}
                      isMultiSelected={selectedAnnotationIds.has(a.id)}
                      selectedAnnotationIds={selectedAnnotationIds}
                      onMultiSelectClick={(isShiftClick) =>
                        handleAnnotationClick(a.id, isShiftClick)
                      }
                      onReorder={handleReorderAnnotations}
                    />
                  ))}
                </EntriesList>
              )}
          </React.Fragment>
        ))}
      </React.Fragment>
    )
  }

  return (
    <Section>
      <SectionTitleRow
        onDragOver={handleAnnotationsSectionDragOver}
        onDragLeave={handleAnnotationsSectionDragLeave}
        onDrop={handleAnnotationsSectionDrop}
        $isDragOver={isAnnotationsSectionDragOver}>
        <SectionTitleContent>
          <TooltipWrapper
            label='Click to edit, Ctrl/Cmd+Click to select multiple, Shift+Click to select range. Drag to reorder or group/ungroup annotations.'
            side='right'
            offset={5}>
            <SectionTitle>{sectionTitle}</SectionTitle>
          </TooltipWrapper>
        </SectionTitleContent>

        <ToggleAllVisibilityButton
          type={historical ? 'historicalAnnotations' : 'annotations'}
          count={sectionAnnotations.count}
        />

        <AnnotationSortButton />

        <CollapseButton
          handleCollapse={(collapsed: boolean) => setIsCollapsed(collapsed)}
        />

        {batchActions && (
          <BatchAnnotationActions
            selectedAnnotationIds={selectedAnnotationIds}
            onClearSelection={handleClearSelection}
            onBatchDelete={handleBatchDelete}
          />
        )}
      </SectionTitleRow>

      {gatekeeper.feature.annotationFormButton && (
        <Link
          to='/kestrel/forms/$inspectionType/$formName'
          params={{
            inspectionType: inspectionType || 'default',
            formName:
              gatekeeper.feature.annotationFormButton.formType.toLowerCase(),
          }}
          search={{
            kestrelModelId: kestrelModel?.id,
          }}>
          <CantileverButton primary>
            View {gatekeeper.feature.annotationFormButton.formType} Form
          </CantileverButton>
        </Link>
      )}

      {renderAnnotations()}

      <ConfirmationModal
        isOpen={showBatchDeleteConfirm}
        title='Delete Annotations'
        message={batchDeleteMessage}
        confirmText='Delete'
        cancelText='Cancel'
        onConfirm={handleBatchDeleteConfirm}
        onCancel={handleBatchDeleteCancel}
      />
    </Section>
  )
}
